/**
 * LocationSelect.jsx — Lawyer Portal
 * Searchable, mobile-friendly State → District/City picker.
 * Styled to match the lawyer portal's light slate design system.
 *
 * Props:
 *   state         {string}   currently selected state name
 *   city          {string}   currently selected city/district name
 *   onStateChange {fn}       called with new state name (string)
 *   onCityChange  {fn}       called with new city name (string)
 *   stateError    {string}   validation error for state
 *   cityError     {string}   validation error for city
 *   disabled      {bool}
 *   labelState    {string}
 *   labelCity     {string}
 */
import { useMemo } from "react";
import Select from "react-select";
import { INDIA_STATES, getCities } from "../lib/indiaLocations";

function buildSelectStyles(hasError) {
  const borderColor = hasError ? "#fca5a5" : "#e2e8f0";
  const focusBorderColor = hasError ? "#ef4444" : "#071b33";

  return {
    control: (base, state) => ({
      ...base,
      minHeight: "48px",
      borderRadius: "10px",
      borderWidth: "1.5px",
      borderColor: state.isFocused ? focusBorderColor : borderColor,
      backgroundColor: hasError ? "#fef2f2" : "#ffffff",
      boxShadow: state.isFocused ? `0 0 0 3px ${hasError ? "rgba(239,68,68,0.15)" : "rgba(7,27,51,0.08)"}` : "none",
      cursor: "pointer",
      transition: "all 0.15s",
      "&:hover": { borderColor: focusBorderColor },
    }),
    placeholder: (base) => ({ ...base, color: "#94a3b8", fontSize: "18px" }),
    singleValue: (base) => ({ ...base, color: "#0f172a", fontSize: "18px", fontWeight: 500 }),
    option: (base, { isSelected, isFocused }) => ({
      ...base,
      fontSize: "18px",
      backgroundColor: isSelected ? "#071b33" : isFocused ? "#f1f5f9" : "#ffffff",
      color: isSelected ? "#ffffff" : "#0f172a",
      cursor: "pointer",
      borderRadius: "6px",
      padding: "12px 14px",
      margin: "1px 2px",
    }),
    menu: (base) => ({
      ...base,
      borderRadius: "12px",
      boxShadow: "0 8px 24px rgba(0,0,0,0.10)",
      border: "1px solid #e2e8f0",
      zIndex: 9999,
    }),
    menuList: (base) => ({ ...base, maxHeight: "250px", padding: "6px" }),
    input: (base) => ({ ...base, fontSize: "18px" }),
    indicatorSeparator: () => ({ display: "none" }),
    dropdownIndicator: (base) => ({ ...base, color: "#94a3b8", padding: "0 8px" }),
    clearIndicator: (base) => ({ ...base, color: "#cbd5e1", "&:hover": { color: "#ef4444" } }),
  };
}

export default function LocationSelect({
  state = "",
  city = "",
  onStateChange,
  onCityChange,
  stateError = "",
  cityError = "",
  disabled = false,
  labelState = "State",
  labelCity = "District",
}) {
  const stateOptions = INDIA_STATES;
  const cityOptions  = useMemo(() => getCities(state), [state]);

  const selectedState = stateOptions.find((o) => o.value === state) || null;
  const selectedCity  = cityOptions.find((o) => o.value === city)   || null;

  const handleStateChange = (option) => {
    onStateChange?.(option ? option.value : "");
    onCityChange?.(""); // reset city on state change
  };

  const labelClass = "block text-xs font-semibold text-slate-600 mb-1.5";

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      {/* State */}
      <div>
        <label className={labelClass}>{labelState}</label>
        <Select
          instanceId="lawyer-state-select"
          options={stateOptions}
          value={selectedState}
          onChange={handleStateChange}
          placeholder="Search state..."
          isSearchable
          isClearable
          isDisabled={disabled}
          styles={buildSelectStyles(!!stateError)}
          noOptionsMessage={() => "No state found"}
        />
        {stateError && (
          <p className="text-xs text-red-500 mt-1">{stateError}</p>
        )}
      </div>

      {/* City / District */}
      <div>
        <label className={labelClass}>{labelCity}</label>
        <Select
          instanceId="lawyer-city-select"
          options={cityOptions}
          value={selectedCity}
          onChange={(o) => onCityChange?.(o ? o.value : "")}
          placeholder={state ? "Search district..." : "Select state first"}
          isSearchable
          isClearable
          isDisabled={disabled || !state}
          styles={buildSelectStyles(!!cityError)}
          noOptionsMessage={() => "No district found"}
        />
        {cityError && (
          <p className="text-xs text-red-500 mt-1">{cityError}</p>
        )}
      </div>
    </div>
  );
}
